"use client";
import {useState, useCallback} from "react";
import styles from './index.module.scss';
import IconButton from "@/app/_components/Shared/IconButton";
import PersonalInfo from "@/app/_components/ProSteps/PersonalInfo";
import CompanyDetails from "@/app/_components/ProSteps/CompanyDetails";
import ProRequirements from "@/app/_components/ProSteps/ProRequirements";
import PackagePreference from "@/app/_components/ProSteps/PackagePreference";
import {useRouter} from "nextjs-toploader/app";
import axios from "axios";
import {toast} from "react-toastify";
import {object, string, array} from "yup";
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const steps = [
    "Personal Information",
    "Company Details",
    "PRO Requirements",
    "Package Preference"
];

const stepsSchema = {
    1: {
        name: string().required('Name is required'),
        phone: string()
            .test('is-valid-uae-phone', 'Please enter a valid UAE phone number', value => {
                if (!value) return false;
                const phone = parsePhoneNumberFromString(value);
                return phone && phone.isValid() && phone.country === 'AE';
            })
            .required('Phone number is required'),
    },
    2: {
        companyName: string().required('Company name is required'),
    },
    3: {
        requestVolume: string().required('Please select estimated monthly service requests'),
        services: array().min(1, 'Please select at least one service').required('Services are required'),
    },
    4: {
        packageType: string().required('Please select a package type'),
    }
};

// merge all schemas into one
const schema = object().shape(Object.assign({}, ...Object.values(stepsSchema)));

export default function ProSteps() {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [data, setData] = useState({
        name: '',
        email: '',
        phone: '',
        companyName: '',
        emirate: '',
        employees: '',
        proStaffing: '',
        requestVolume: '',
        services: [],
        additionalServices: '',
        packageType: '',
    });

    const sendStepData = useCallback(async (currentStep, currentData) => {
        try {
            let message = 'Type: PRO Services Request (Partial)\n';
            message += `Step Completed: ${currentStep} of 4\n`;
            message += `Name: ${currentData.name || 'N/A'}\n`;
            if (currentData.email) message += `Email: ${currentData.email}\n`;
            if (currentData.phone) message += `Phone: ${currentData.phone}\n`;
            if (currentData.companyName) message += `Company: ${currentData.companyName}\n`;
            if (currentData.emirate) message += `Emirate: ${currentData.emirate}\n`;
            if (currentData.employees) message += `Employees: ${currentData.employees}\n`;
            if (currentData.proStaffing) message += `PRO Staffing: ${currentData.proStaffing}\n`;
            if (currentData.requestVolume) message += `Monthly Request Volume: ${currentData.requestVolume}\n`;
            if (currentData.services?.length) message += `Services: ${currentData.services.join(', ')}\n`;
            if (currentData.additionalServices) message += `Additional Requirements: ${currentData.additionalServices}\n`;
            if (currentData.packageType) message += `Package Type: ${currentData.packageType}\n`;

            await axios.post(process.env.NEXT_PUBLIC_API_URL + '/tickets', {
                name: currentData.name || 'Incomplete Form',
                type: 'pro_services',
                email: currentData.email || '',
                phone: currentData.phone || '',
                message,
                data: {
                    ...currentData,
                    stepCompleted: currentStep,
                    isPartial: true
                }
            });
        } catch (error) {
            // Silently fail - don't block user progression
            console.error('Failed to save step data:', error);
        }
    }, []);

    const handleNext = useCallback(() => {
        const stepSchema = object().shape(stepsSchema[step]);
        stepSchema.validate(data).then(() => {
            // Send current step data to backend
            sendStepData(step, data);
            setStep(step + 1);
        }).catch(error => {
            toast.error(error.message);
        });
    }, [data, step, sendStepData]);

    const handleBack = useCallback(() => {
        setStep(step - 1);
    }, [step]);

    const handleChange = useCallback((key, value) => {
        setData({...data, [key]: value});
    }, [data]);

    const submit = useCallback(async () => {
        setIsLoading(true);
        schema.validate(data).then(() => {
            // Generate a formatted message
            let message = 'Type: PRO Services Request\n';
            message += `Package Type: ${data.packageType}\n`;
            message += `Name: ${data.name}\n`;
            if (data.email) message += `Email: ${data.email}\n`;
            message += `Phone: ${data.phone}\n`;
            message += `Company: ${data.companyName}\n`;
            message += `Emirate: ${data.emirate}\n`;
            if (data.employees) message += `Employees: ${data.employees}\n`;
            if (data.proStaffing) message += `PRO Staffing: ${data.proStaffing}\n`;
            message += `Monthly Request Volume: ${data.requestVolume}\n`;
            message += `Services: ${data.services.join(', ')}\n`;
            if (data.additionalServices) message += `Additional Requirements: ${data.additionalServices}\n`;

            return axios.post(process.env.NEXT_PUBLIC_API_URL + '/tickets', {
                name: data.name,
                type: 'pro_services',
                email: data.email || '',
                phone: data.phone,
                message,
                data: {
                    ...data,
                    stepCompleted: 4,
                    isPartial: false
                }
            }).then(response => {
                // Redirect to thank you page
                router.push('/pro-services-request/thank-you');
            }).catch(error => {
                toast.error('Failed to send request. Please try again later');
            });
        }).catch(error => {
            toast.error(error.message);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [data, router])

    return (
        <div className="container-wrapper">
            <div className={styles.container}>
                <div className="container-wrapper">
                    <div className={styles.sidebar}>
                        <div className="container-wrapper">
                            <h3 className={styles.sidebarTitle}>PRO Services Request</h3>
                            {steps.map((stepName, index) => (
                                <div key={index} className={`${styles.step} ${step === index + 1 ? styles.active : ''} ${step > index + 1 ? styles.completed : ''}`}>
                                    <span className={styles.stepNumber}>{index + 1}</span>
                                    <span className={styles.stepName}>{stepName}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={styles.form}>
                        <div className="container-wrapper">
                            {step === 1 && <PersonalInfo data={data} onChange={handleChange} />}
                            {step === 2 && <CompanyDetails data={data} onChange={handleChange} />}
                            {step === 3 && <ProRequirements data={data} onChange={handleChange} />}
                            {step === 4 && <PackagePreference data={data} onChange={handleChange} />}

                            <div className={styles.buttons}>
                                {step > 1 && (
                                    <IconButton color="secondary" onClick={handleBack} disabled={isLoading}>
                                        Previous
                                    </IconButton>
                                )}
                                {step < 4 && (
                                    <IconButton onClick={handleNext}>
                                        Next
                                    </IconButton>
                                )}
                                {step === 4 && (
                                    <IconButton onClick={submit} isLoading={isLoading} disabled={isLoading}>
                                        Submit Request
                                    </IconButton>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
